import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import ArticleContent from '../blog-post-detail/components/ArticleContent';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { postsService } from '../../services/postsService';
import DeleteConfirmationModal from '../admin-posts-management/components/DeleteConfirmationModal';

const AdminPostPreview = () => {
  const { id: postId } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    // Validate postId before making API call
    if (!postId || postId === ':id') {
      setError('Invalid post ID. Please select a post from the posts list.');
      setLoading(false);
      return;
    }
    
    if (postId) {
      loadPost();
    } else {
      setError('No post ID provided');
      setLoading(false);
    }
  }, [postId]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const data = await postsService?.getPostById(postId);
      setPost(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load post');
      console.error('Load post error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!post) return;
    
    try {
      setIsUpdating(true);
      const newStatus = post?.status === 'published' ? 'draft' : 'published';
      const updatedPost = await postsService?.updatePost(post?.id, {
        ...post,
        status: newStatus,
        publishedAt: newStatus === 'published' ? new Date()?.toISOString() : null
      });
      setPost(updatedPost);
    } catch (err) {
      console.error('Toggle publish error:', err);
      alert('Failed to update post status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/posts/${post?.id}`);
  };

  const handleDelete = async () => {
    try {
      await postsService?.deletePost(post?.id);
      navigate('/admin-posts-management');
    } catch (err) {
      console.error('Delete post error:', err);
      alert('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateReadTime = (content) => {
    if (!content) return 0;
    const wordsPerMinute = 200;
    const wordCount = content?.split(/\s+/)?.length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="content-area">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading post preview...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="content-area">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-destructive mb-4">{error || 'Post not found'}</p>
              <Button onClick={() => navigate('/admin-posts-management')}>Back to Posts</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Posts', path: '/admin-posts-management' },
    { label: 'Preview', path: `/admin/posts/preview/${postId}` }
  ];

  // Only show public link if post is published AND has a slug
  const canViewPublic = post?.status === 'published' && post?.slug;
  const publicUrl = canViewPublic ? `/blog/${post?.slug}` : null;

  return (
    <>
      <AdminNavigation />
      <div className="content-area min-h-screen bg-background">
        <div className="max-w-4xl mx-auto py-6 md:py-8 lg:py-10">
          <BreadcrumbNavigation customItems={breadcrumbItems} />

          {/* Action Toolbar */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-border">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/admin-posts-management')}
            >
              Back to Posts
            </Button>

            <div className="flex flex-wrap items-center gap-3">
              {publicUrl ? (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="left"
                  onClick={() => window.open(publicUrl, '_blank')}
                >
                  View public post
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground px-3 py-2 bg-muted rounded-md">
                  Public view unavailable (draft)
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                iconName={post?.status === 'published' ? 'EyeOff' : 'Eye'}
                iconPosition="left"
                onClick={handleTogglePublish}
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : (post?.status === 'published' ? 'Unpublish' : 'Publish')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                onClick={handleEdit}
              >
                Edit
              </Button>
            </div>
          </div>

          {/* Post Metadata */}
          <div className="mt-8 p-6 bg-card border border-border rounded-lg">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-xl font-semibold text-foreground">
                    {post?.title}
                  </h1>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                    post?.status === 'published' ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon 
                      name={post?.status === 'published' ? 'CheckCircle2' : 'Clock'} 
                      size={14} 
                      color="currentColor" 
                    />
                    {post?.status === 'published' ? 'Published' : 'Draft'}
                  </div>
                </div>
                {post?.excerpt && (
                  <p className="text-base text-muted-foreground mb-4">
                    {post?.excerpt}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="Calendar" size={16} color="var(--color-primary)" />
                <span>Created: {formatDate(post?.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="Clock" size={16} color="var(--color-primary)" />
                <span>Updated: {formatDate(post?.updatedAt)}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="BookOpen" size={16} color="var(--color-primary)" />
                <span>Read time: {calculateReadTime(post?.content)} min</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="Link" size={16} color="var(--color-primary)" />
                <span>Slug: /{post?.slug}</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          {post?.heroImage && (
            <div className="mt-8">
              <div className="relative w-full h-64 md:h-80 lg:h-96 overflow-hidden rounded-lg">
                <Image
                  src={post?.heroImage}
                  alt={post?.heroImageAlt || post?.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              </div>
            </div>
          )}

          {/* Rendered Content */}
          <div className="mt-8 md:mt-10 lg:mt-12">
            <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Eye" size={16} color="var(--color-primary)" />
              <span>Preview - Content as it appears to readers</span>
            </div>
            <ArticleContent
              content={post?.content}
              embeddedImages={post?.embeddedImages || []}
            />
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        postCount={1}
      />
    </>
  );
};

export default AdminPostPreview;